import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataErrorScreen } from './components/DataErrorScreen';
import { dataClient } from './data/client';
import type { AggregatesArtifact, ResponsesArtifact } from './data/types';
import { FoundationHarness } from './dev/FoundationHarness';
import { OpeningSequence } from './sections/opening/OpeningSequence';
import { StoryExperience } from './sections/story/StoryExperience';

export default function App() {
  const [aggregates, setAggregates] = useState<AggregatesArtifact | null>(null);
  const [responses, setResponses] = useState<ResponsesArtifact | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [introComplete, setIntroComplete] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const showHarness = useMemo(
    () => import.meta.env.DEV && new URLSearchParams(window.location.search).get('debug') === 'foundation',
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);
    dataClient.loadAggregates()
      .then((value) => {
        if (!cancelled) setAggregates(value);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause : new Error(String(cause)));
      });

    const startResponses = () => {
      dataClient.preloadResponses()
        .then((value) => {
          if (!cancelled) setResponses(value);
        })
        .catch((cause: unknown) => {
          if (!cancelled) setError(cause instanceof Error ? cause : new Error(String(cause)));
        });
    };
    const frame = requestAnimationFrame(startResponses);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [attempt]);

  const retry = useCallback(() => {
    dataClient.retry();
    setAggregates(null);
    setResponses(null);
    setAttempt((value) => value + 1);
  }, []);
  const completeIntro = useCallback(() => setIntroComplete(true), []);

  if (error) return <DataErrorScreen error={error} onRetry={retry} />;
  if (showHarness && responses) return <FoundationHarness responses={responses} />;

  return (
    <>
      {aggregates && introComplete && <StoryExperience aggregates={aggregates} responses={responses} introComplete={introComplete} />}
      {!introComplete && <OpeningSequence onComplete={completeIntro} />}
    </>
  );
}
